import { formatCurrency } from '@angular/common';
import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ILiquidation } from '@core/models/ILiquidation';
import { LiquidationModel } from 'src/app/products/components/liquidation-section/components/liquidation-card/liquidation-card.model';
import { BehaviorSubject, combineLatest, map, shareReplay, Subscription } from 'rxjs';
import { PiramidalService } from './piramidal.service';
import { HttpClient } from '@angular/common/http';
import { Product } from '@core/models/piramide/Product';

@Injectable({
  providedIn: 'root',
})
export class PromosService {
  private selectedLiquidationLocal: LiquidationModel = null;
  private selectedLiquidationStatusSubscription: Subscription | null = null;
  public selectedLiquidation: BehaviorSubject<LiquidationModel> = new BehaviorSubject(null);
  public liquidations: BehaviorSubject<LiquidationModel[]> = new BehaviorSubject(null);

  public readonly liquidations$ = this.http
    .get<ILiquidation[]>('https://static.compragamer.com/liquidaciones')
    .pipe(shareReplay(1));
  constructor(
    private _activatedRoute: ActivatedRoute,
    private _piramidalService: PiramidalService,
    private http?: HttpClient,
  ) {}

  public getDiscountPrice(
    liquidationName: string,
    previousPrice: number,
    specialPrice: number,
  ): string {
    const discount = previousPrice - specialPrice;
    if (discount < 5000) {
      if (liquidationName) {
        return `Descuento ${liquidationName}`;
      } else {
        return 'Con descuento';
      }
    } else {
      return formatCurrency(discount, 'es-Ar', '$', 'ARS', '0.0') + ' de descuento';
    }
  }

  private filterLiquidationsWithActiveProducts(
    liquidations: ILiquidation[],
    products: Product[],
  ): ILiquidation[] {
    const activeProductIds = new Set(products.map((product) => product.id_producto));
    return liquidations.filter((liquidation) =>
      liquidation.idProductos.some((idProducto) => activeProductIds.has(idProducto)),
    );
  }

  private findSelectedLiquidation(
    filteredLiquidationsModels: LiquidationModel[],
    liquidationUrl: string,
  ): void {
    const selectedLiquidation = filteredLiquidationsModels.find((liquidation) => {
      return (
        liquidation.url === liquidationUrl &&
        (liquidation.status() == 'active' || liquidation.status() == 'pending')
      );
    });
    this.selectedLiquidationLocal = selectedLiquidation ? selectedLiquidation : null;
    if (this.selectedLiquidationLocal) {
      this.selectedLiquidationLocal.onSelected = (p) =>
        this.soyLiquidacionSeleccionada(this.selectedLiquidationLocal.id);

      this.selectedLiquidationStatusSubscription = this.selectedLiquidationLocal.status$?.subscribe(
        (status) => {
          if (status === 'ended' || status === 'hide') {
            this.selectedLiquidationLocal = null;
            this.selectedLiquidation.next(null);
            this.selectedLiquidationStatusSubscription?.unsubscribe();
          }
        },
      );
    } else {
      this.selectedLiquidation.next(null);
    }
    this.selectedLiquidation.next(this.selectedLiquidationLocal);
  }

  public getLiquidations(): void {
    combineLatest([
      this.liquidations$,
      this._activatedRoute.queryParams,
      this._piramidalService.products.observable,
    ])
      .pipe(
        map(([liquidations, queryParams, products]) => {
          const liquidationUrl = queryParams['liqui']?.toLowerCase();
          liquidations = liquidations.filter(this.isValidLiqui).map((liqui) => ({
            ...liqui,
            imagenes: {
              ...liqui.imagenes,
              banner: this.encodeImage(liqui.imagenes.banner),
            },
          }));
          const filteredLiquidations = this.filterLiquidationsWithActiveProducts(
            liquidations,
            products,
          );
          const filteredLiquidationModels = filteredLiquidations.map((liq) => {
            const model = new LiquidationModel(liq);
            model.onSelected = () => this.soyLiquidacionSeleccionada(model.id);
            return model;
          });

          products.forEach((product) => {
            product.$liquidation = filteredLiquidationModels.filter((l) =>
              l.idProducts.includes(product.id_producto),
            );
          });
          if (liquidationUrl) {
            this.findSelectedLiquidation(filteredLiquidationModels, liquidationUrl);
          } else {
            this.selectedLiquidationLocal = null;
            this.selectedLiquidation.next(this.selectedLiquidationLocal);
          }

          this.liquidations.next(filteredLiquidationModels);
        }),
      )
      .subscribe();
  }

  private soyLiquidacionSeleccionada(idLiquidacion: number): boolean {
    return (
      this.selectedLiquidationLocal &&
      this.selectedLiquidationLocal.id == idLiquidacion &&
      (this.selectedLiquidationLocal.status() == 'active' ||
        this.selectedLiquidationLocal.status() == 'pending')
    );
  }

  private encodeImage(image) {
    return image ? encodeURI(image) : null;
  }

  private isValidLiqui(liquidation: ILiquidation): boolean {
    return !!(liquidation.imagenes && liquidation.nombre && liquidation.idProductos);
  }
}
